import { ref, computed } from 'vue';
import { DiscordSDK } from '@discord/embedded-app-sdk';
import type { DiscordUser, Participant } from '../types';
import { apiUrl } from '../net';

export function useDiscordSdk() {
  const isInsideDiscord = ref(false);
  const isReady = ref(false);
  const currentUser = ref<DiscordUser | null>(null);
  // Recupera sala salva para NUNCA cair em default-room
  const savedRoom = (typeof window !== 'undefined')
    ? (sessionStorage.getItem('nexia_room_id') || localStorage.getItem('nexia_room_id') || '')
    : '';
  const initialRoom = savedRoom && savedRoom !== 'default-room' ? savedRoom : 'sala-principal';
  const channelId = ref<string>(initialRoom);
  const guildId = ref<string | null>(null);
  const authStatus = ref<'idle' | 'authorizing' | 'authenticated' | 'error' | 'standalone'>('idle');
  const errorMessage = ref<string | null>(null);
  const discordParticipants = ref<Participant[]>([]);

  let discordSdk: DiscordSDK | null = null;

  function getDiscordAvatarUrl(userId: string, avatarHash?: string | null): string {
    if (avatarHash) {
      const ext = avatarHash.startsWith('a_') ? 'gif' : 'png';
      return `https://cdn.discordapp.com/avatars/${userId}/${avatarHash}.${ext}?size=128`;
    }
    try {
      const defaultIndex = Math.abs(Number(BigInt(userId || '0') % 5n));
      return `https://cdn.discordapp.com/embed/avatars/${defaultIndex}.png`;
    } catch {
      return 'https://cdn.discordapp.com/embed/avatars/0.png';
    }
  }

  const userAvatar = computed(() => {
    if (!currentUser.value) return 'https://cdn.discordapp.com/embed/avatars/0.png';
    return getDiscordAvatarUrl(currentUser.value.id, currentUser.value.avatar);
  });

  const userName = computed(() => {
    return currentUser.value?.global_name || currentUser.value?.username || 'Usuário';
  });

  async function initDiscord() {
    const urlParams = new URLSearchParams(window.location.search);
    const hasFrameId = urlParams.has('frame_id') || urlParams.has('instance_id');
    const clientId = import.meta.env.VITE_DISCORD_CLIENT_ID || '1551969155039629472';

    if (hasFrameId && typeof window !== 'undefined' && window.parent !== window) {
      try {
        isInsideDiscord.value = true;
        authStatus.value = 'authorizing';

        discordSdk = new DiscordSDK(clientId);
        await discordSdk.ready();

        if (discordSdk.channelId) {
          channelId.value = discordSdk.channelId;
          try {
            sessionStorage.setItem('nexia_room_id', discordSdk.channelId);
            localStorage.setItem('nexia_room_id', discordSdk.channelId);
          } catch (e) {}
        }
        guildId.value = discordSdk.guildId || null;

        // 1. Autenticação OAuth2 do Discord com fallback para consentimento
        let code: string | null = null;
        const scopes = discordSdk.guildId ? ['identify', 'guilds'] : ['identify'];

        try {
          const authRes = await discordSdk.commands.authorize({
            client_id: clientId,
            response_type: 'code',
            state: '',
            prompt: 'none',
            scope: scopes as any,
          });
          code = authRes.code;
        } catch (silentErr) {
          console.log('[DiscordSDK] Autorização silenciosa falhou, abrindo modal OAuth nativo...');
          try {
            const authRes = await discordSdk.commands.authorize({
              client_id: clientId,
              response_type: 'code',
              state: '',
              scope: scopes as any,
            });
            code = authRes.code;
          } catch (consentErr) {
            console.warn('[DiscordSDK] Consentimento não concedido:', consentErr);
          }
        }

        if (code) {
          try {
            const tokenEndpoint = apiUrl('/api/token');
            const res = await fetch(tokenEndpoint, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ code }),
            });

            if (res.ok) {
              const data = await res.json();
              const { access_token } = data;
              if (access_token) {
                const auth = await discordSdk.commands.authenticate({ access_token });
                if (auth && auth.user) {
                  currentUser.value = {
                    id: auth.user.id,
                    username: auth.user.username,
                    global_name: auth.user.global_name || auth.user.username,
                    avatar: auth.user.avatar,
                  };
                  console.log('[DiscordSDK] Autenticado com sucesso como:', currentUser.value.global_name);
                }
              }
            } else {
              console.warn('[DiscordSDK] Resposta do /api/token:', res.status, await res.text());
            }
          } catch (tokenErr) {
            console.warn('[DiscordSDK] Falha na troca do token OAuth2:', tokenErr);
          }
        }

        // 2. Buscar participantes conectados na atividade
        try {
          const res = await discordSdk.commands.getInstanceConnectedParticipants();
          if (res && Array.isArray(res.participants)) {
            updateNativeParticipants(res.participants);
          }

          await discordSdk.subscribe('ACTIVITY_INSTANCE_PARTICIPANTS_UPDATE', (event: any) => {
            if (event && Array.isArray(event.participants)) {
              updateNativeParticipants(event.participants);
            }
          });
        } catch (sdkPartErr) {
          console.warn('[DiscordSDK] Falha ao obter participantes da atividade:', sdkPartErr);
        }

        // 3. Buscar membros do canal de voz como reforço
        try {
          if (discordSdk.channelId) {
            const channel = await (discordSdk.commands as any).getChannel({ channel_id: discordSdk.channelId });
            if (channel && Array.isArray(channel.voice_states)) {
              updateVoiceStates(channel.voice_states);
            }
          }
        } catch (chanErr) {
          console.warn('[DiscordSDK] Falha ao obter membros do canal de voz:', chanErr);
        }

        // 4. Se o usuário ainda não tiver perfil completo, usa identificador único aleatório
        if (!currentUser.value) {
          const randomSuffix = Math.floor(100 + Math.random() * 900);
          currentUser.value = {
            id: `anon-${randomSuffix}`,
            username: `Amigo ${randomSuffix}`,
            global_name: `Amigo ${randomSuffix}`,
          };
        }

        authStatus.value = 'authenticated';
        isReady.value = true;
      } catch (err: any) {
        console.warn('[DiscordSDK] Erro geral ao inicializar no Discord:', err);
        setupStandaloneMock();
      }
    } else {
      setupStandaloneMock();
    }
  }

  function updateNativeParticipants(participants: any[]) {
    if (!Array.isArray(participants)) return;
    const mapped = participants.map((p) => {
      const displayName = p.nickname || p.global_name || p.username || 'Amigo';
      return {
        id: p.id,
        name: displayName,
        avatarUrl: getDiscordAvatarUrl(p.id, p.avatar),
        isStreaming: false,
        isSelf: p.id === currentUser.value?.id,
      };
    });

    mapped.forEach((m) => {
      const idx = discordParticipants.value.findIndex((dp) => dp.id === m.id);
      if (idx >= 0) {
        discordParticipants.value[idx] = m;
      } else {
        discordParticipants.value.push(m);
      }
    });

    // Se o usuário atual ainda tiver nome provisório (Amigo xxx), sincroniza com o membro real correspondente
    if (currentUser.value && currentUser.value.username.startsWith('Amigo')) {
      const self = participants.find((p) => p.id === currentUser.value?.id) || participants[0];
      if (self) {
        currentUser.value = {
          id: self.id || currentUser.value.id,
          username: self.username || currentUser.value.username,
          global_name: self.nickname || self.global_name || self.username || currentUser.value.global_name,
          avatar: self.avatar || currentUser.value.avatar,
        };
      }
    }
  }

  function updateVoiceStates(voiceStates: any[]) {
    if (!Array.isArray(voiceStates)) return;
    const mapped = voiceStates
      .filter((vs) => vs && vs.user)
      .map((vs) => {
        const u = vs.user;
        const displayName = vs.nick || u.global_name || u.username || 'Amigo';
        return {
          id: u.id,
          name: displayName,
          avatarUrl: getDiscordAvatarUrl(u.id, u.avatar),
          isStreaming: false,
          isSelf: u.id === currentUser.value?.id,
        };
      });

    mapped.forEach((m) => {
      const idx = discordParticipants.value.findIndex((dp) => dp.id === m.id);
      if (idx >= 0) {
        discordParticipants.value[idx] = m;
      } else {
        discordParticipants.value.push(m);
      }
    });

    if (currentUser.value && currentUser.value.username.startsWith('Amigo')) {
      const self = voiceStates.find((vs) => vs.user?.id === currentUser.value?.id) || voiceStates[0];
      if (self?.user) {
        currentUser.value = {
          id: self.user.id,
          username: self.user.username,
          global_name: self.nick || self.user.global_name || self.user.username,
          avatar: self.user.avatar,
        };
      }
    }
  }

  function setupStandaloneMock() {
    isInsideDiscord.value = false;
    authStatus.value = 'standalone';

    const params = new URLSearchParams(window.location.search);
    const saved = (typeof window !== 'undefined')
      ? (sessionStorage.getItem('nexia_room_id') || localStorage.getItem('nexia_room_id') || '')
      : '';
    const urlRoom = params.get('room') || (saved && saved !== 'default-room' ? saved : 'sala-amigos-1');
    const urlUser = params.get('streamer_name') || params.get('user');

    const randomId = Math.random().toString(36).substring(2, 8);
    const displayName = urlUser || `Amigo_${randomId.slice(0, 4).toUpperCase()}`;

    channelId.value = urlRoom;
    try {
      sessionStorage.setItem('nexia_room_id', urlRoom);
      localStorage.setItem('nexia_room_id', urlRoom);
    } catch (e) {}
    currentUser.value = {
      id: `streamer_${randomId}`,
      username: displayName.toLowerCase().replace(/\s+/g, '_'),
      global_name: displayName,
      avatar: null,
    };

    isReady.value = true;
  }

  function openBroadcasterInBrowser() {
    const baseOrigin = window.location.origin.includes('discordsays.com')
      ? 'https://golive-discord-activity.nexia-app.workers.dev'
      : window.location.origin;
    const webUrl = `${baseOrigin}?room=${encodeURIComponent(channelId.value)}&streamer_name=${encodeURIComponent(userName.value)}&mode=broadcaster`;
    if (isInsideDiscord.value && discordSdk) {
      discordSdk.commands.openExternalLink({ url: webUrl }).catch(() => {
        window.open(webUrl, '_blank');
      });
    } else {
      window.open(webUrl, '_blank');
    }
  }

  async function loginDiscord() {
    const clientId = import.meta.env.VITE_DISCORD_CLIENT_ID || '1551969155039629472';
    if (!discordSdk) return;
    try {
      const scopes = discordSdk.guildId ? ['identify', 'guilds'] : ['identify'];
      const authRes = await discordSdk.commands.authorize({
        client_id: clientId,
        response_type: 'code',
        state: '',
        scope: scopes as any,
      });
      if (authRes.code) {
        const res = await fetch(apiUrl('/api/token'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code: authRes.code }),
        });
        if (res.ok) {
          const { access_token } = await res.json();
          if (access_token) {
            const auth = await discordSdk.commands.authenticate({ access_token });
            if (auth?.user) {
              currentUser.value = {
                id: auth.user.id,
                username: auth.user.username,
                global_name: auth.user.global_name || auth.user.username,
                avatar: auth.user.avatar,
              };
              console.log('[DiscordSDK] Autenticado com sucesso via login:', currentUser.value.global_name);
            }
          }
        }
      }
      const resPart = await discordSdk.commands.getInstanceConnectedParticipants();
      if (resPart?.participants) updateNativeParticipants(resPart.participants);
      if (discordSdk.channelId) {
        const ch = await (discordSdk.commands as any).getChannel({ channel_id: discordSdk.channelId });
        if (ch?.voice_states) updateVoiceStates(ch.voice_states);
      }
    } catch (err) {
      console.warn('[DiscordSDK] Erro no login manual:', err);
    }
  }

  return {
    discordSdk,
    isInsideDiscord,
    isReady,
    currentUser,
    channelId,
    guildId,
    authStatus,
    errorMessage,
    userAvatar,
    userName,
    discordParticipants,
    initDiscord,
    loginDiscord,
    openBroadcasterInBrowser,
  };
}
